<template>
  <main class="periods-page">
    <header class="page-heading">
      <div>
        <span class="eyebrow">CONTROL OPERATIVO</span>
        <h1>Periods</h1>
        <p>Website ranges and payment dates used throughout Modato.</p>
      </div>
      <div class="page-actions">
        <v-chip v-if="!loading && !canEdit" size="small" color="grey" variant="tonal">View Only</v-chip>
        <v-btn
          v-if="canEdit"
          variant="outlined"
          prepend-icon="mdi-calendar-star"
          :disabled="loading || !periods.length"
          @click="openNotes"
        >
          Holidays & Notes
        </v-btn>
        <v-btn
          v-if="canEdit"
          color="primary"
          prepend-icon="mdi-plus"
          :disabled="loading || !periods.length"
          @click="openNextPeriod"
        >
          Add Next Period
        </v-btn>
      </div>
    </header>

    <v-card class="period-controls" elevation="0">
      <div class="year-control">
        <v-btn icon="mdi-chevron-left" variant="text" aria-label="Previous year" @click="changeYear(-1)" />
        <strong>{{ selectedYear }}</strong>
        <v-btn icon="mdi-chevron-right" variant="text" aria-label="Next year" @click="changeYear(1)" />
      </div>
      <div class="system-title">
        <small>PAYMENT CALENDAR</small>
        <strong>Split Payment System</strong>
      </div>
      <v-btn variant="text" size="small" @click="selectedYear = currentYear">This Year</v-btn>
    </v-card>

    <v-alert v-if="error" type="warning" variant="tonal" closable class="mb-4" @click:close="error = ''">
      {{ error }}
    </v-alert>

    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate color="primary" />
      <span>Loading periods…</span>
    </div>

    <template v-else-if="periods.length">
      <section v-if="featuredPeriods.length" class="featured-periods" aria-label="Current and next periods">
        <div
          v-for="featured in featuredPeriods"
          :key="`featured-${featured.period.id}`"
          :class="['featured-period', featured.kind]"
        >
          <div class="featured-label">
            <v-icon size="16">{{ featured.kind === 'current' ? 'mdi-calendar-check-outline' : 'mdi-calendar-arrow-right' }}</v-icon>
            <span>{{ featured.label }}</span>
          </div>
          <v-card class="period-card featured-card" elevation="0">
            <div class="period-card-heading">
              <div><strong>{{ featured.period.name }}</strong></div>
              <div class="period-card-actions">
                <v-chip v-if="featured.period.is_locked" size="x-small" color="grey" variant="tonal" prepend-icon="mdi-lock-outline">Locked</v-chip>
                <v-btn
                  v-if="canEdit"
                  icon="mdi-pencil-outline"
                  size="x-small"
                  variant="text"
                  :aria-label="`Edit ${featured.period.name}`"
                  @click="openEdit(featured.period)"
                />
              </div>
            </div>
            <table>
              <thead>
                <tr><th>Site</th><th>Period Start</th><th>Period End</th><th>Payment Date</th></tr>
              </thead>
              <tbody>
                <tr v-for="range in featured.period.ranges" :key="range.website">
                  <td>{{ range.website }}</td>
                  <td>{{ dateLabel(range.start_date) }}</td>
                  <td>{{ dateLabel(range.end_date) }}</td>
                  <td :class="{ 'cb-payment': range.source_website === 'Chaturbate' }">
                    {{ dateLabel(range.source_website === 'Chaturbate' ? featured.period.cb_payment_date : featured.period.payment_date) }}
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="featured.period.notes.length || paymentsAligned(featured.period)" class="period-notes">
              <div v-if="paymentsAligned(featured.period)" class="period-note aligned-note">
                <v-icon size="13">mdi-check-circle-outline</v-icon>
                <span>Payments aligned</span>
              </div>
              <div v-for="note in featured.period.notes" :key="note.id" :class="['period-note', note.note_type === 'General' ? 'general-note' : 'holiday-note']">
                <v-icon size="13">{{ note.note_type === 'Holiday' ? 'mdi-calendar-star' : 'mdi-information-outline' }}</v-icon>
                <span><template v-if="note.note_date"><strong>{{ shortDate(note.note_date) }}</strong> · </template>{{ note.note }}<template v-if="note.country"> · {{ note.country }}</template></span>
              </div>
            </div>
          </v-card>
        </div>
      </section>

      <div class="all-periods-heading">
        <span>ALL {{ selectedYear }} PERIODS</span>
      </div>

      <div class="period-grid">
      <v-card
        v-for="period in periods"
        :key="period.id"
        :class="['period-card', { current: isCurrentPeriod(period) }]"
        elevation="0"
      >
        <div class="period-card-heading">
          <div>
            <strong>{{ period.name }}</strong>
            <span v-if="isCurrentPeriod(period)">Current Period</span>
          </div>
          <div class="period-card-actions">
            <v-chip v-if="period.is_locked" size="x-small" color="grey" variant="tonal" prepend-icon="mdi-lock-outline">Locked</v-chip>
            <v-btn
              v-if="canEdit"
              icon="mdi-pencil-outline"
              size="x-small"
              variant="text"
              :aria-label="`Edit ${period.name}`"
              @click="openEdit(period)"
            />
          </div>
        </div>
        <table>
          <thead>
            <tr><th>Site</th><th>Period Start</th><th>Period End</th><th>Payment Date</th></tr>
          </thead>
          <tbody>
            <tr v-for="range in period.ranges" :key="range.website">
              <td>{{ range.website }}</td>
              <td>{{ dateLabel(range.start_date) }}</td>
              <td>{{ dateLabel(range.end_date) }}</td>
              <td :class="{ 'cb-payment': range.source_website === 'Chaturbate' }">
                {{ dateLabel(range.source_website === 'Chaturbate' ? period.cb_payment_date : period.payment_date) }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="period.notes.length || paymentsAligned(period)" class="period-notes">
          <div v-if="paymentsAligned(period)" class="period-note aligned-note">
            <v-icon size="13">mdi-check-circle-outline</v-icon>
            <span>Payments aligned</span>
          </div>
          <div v-for="note in period.notes" :key="note.id" :class="['period-note', note.note_type === 'General' ? 'general-note' : 'holiday-note']">
            <v-icon size="13">{{ note.note_type === 'Holiday' ? 'mdi-calendar-star' : 'mdi-information-outline' }}</v-icon>
            <span><template v-if="note.note_date"><strong>{{ shortDate(note.note_date) }}</strong> · </template>{{ note.note }}<template v-if="note.country"> · {{ note.country }}</template></span>
          </div>
        </div>
      </v-card>
      </div>
    </template>

    <v-card v-else class="empty-state" elevation="0">
      <v-icon size="42" color="grey-lighten-1">mdi-calendar-blank-outline</v-icon>
      <strong>No periods found for {{ selectedYear }}</strong>
      <span>Choose another year or create the next period.</span>
    </v-card>

    <v-dialog v-model="dialogOpen" max-width="920" persistent>
      <v-card class="period-dialog">
        <v-card-title>{{ editingId ? 'Edit Period' : 'Add Next Period' }}</v-card-title>
        <v-card-subtitle v-if="draft.is_locked">
          This period is locked. Its payment dates and name can be corrected, but its website ranges are protected.
        </v-card-subtitle>
        <v-card-text>
          <div class="period-details-fields">
            <v-text-field v-model.trim="draft.name" label="Period Name" placeholder="1-Animal-2027" variant="outlined" />
            <v-text-field v-model="draft.payment_date" type="date" label="Regular Payment Date" variant="outlined" />
            <v-text-field v-model="draft.cb_payment_date" type="date" label="Chaturbate Payment Date" variant="outlined" :disabled="draft.cb_skipped" />
          </div>
          <v-checkbox v-model="draft.cb_skipped" label="No Chaturbate payment this period (skipped)" hide-details :disabled="draft.is_locked" />
          <div class="ranges-heading">
            <div>
              <strong>Website Date Ranges</strong>
              <small>Streamate is displayed as one range and stored internally as its two weekly ranges.</small>
            </div>
          </div>
          <div class="range-editor">
            <div class="range-editor-head"><span>Site</span><span>Period Start</span><span>Period End</span></div>
            <div v-for="range in draft.ranges" :key="range.website" class="range-editor-row">
              <strong>{{ range.website }}</strong>
              <v-text-field v-model="range.start_date" type="date" density="compact" variant="outlined" hide-details :disabled="draft.is_locked || (range.website === 'Chaturbate' && draft.cb_skipped)" />
              <v-text-field v-model="range.end_date" type="date" density="compact" variant="outlined" hide-details :disabled="draft.is_locked || (range.website === 'Chaturbate' && draft.cb_skipped)" />
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="dialogOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="saving" @click="savePeriod">Save Period</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="notesDialogOpen" max-width="820">
      <v-card class="notes-dialog">
        <v-card-title>Holidays & Period Notes · {{ selectedYear }}</v-card-title>
        <v-card-subtitle>Add a holiday or explanation beneath the period where it matters.</v-card-subtitle>
        <v-card-text>
          <div class="note-form">
            <v-select v-model="noteForm.period_id" :items="periods" item-title="name" item-value="id" label="Period" variant="outlined" />
            <v-select v-model="noteForm.note_type" :items="['Holiday', 'General']" label="Type" variant="outlined" />
            <v-text-field v-if="noteForm.note_type === 'Holiday'" v-model="noteForm.note_date" type="date" label="Holiday Date" variant="outlined" />
            <v-combobox v-if="noteForm.note_type === 'Holiday'" v-model="noteForm.country" :items="['Colombia', 'USA', 'Colombia / USA']" label="Country" variant="outlined" />
            <v-text-field v-model.trim="noteForm.note" :label="noteForm.note_type === 'Holiday' ? 'Holiday Name' : 'Note'" variant="outlined" class="note-text-field" />
            <v-btn color="primary" variant="flat" :loading="savingNote" @click="saveNote">{{ editingNoteId ? 'Update' : 'Add' }}</v-btn>
            <v-btn v-if="editingNoteId" variant="text" @click="resetNoteForm">Cancel Edit</v-btn>
          </div>
          <div class="notes-list">
            <div v-for="note in yearNotes" :key="note.id" class="notes-list-row">
              <div>
                <strong>{{ note.period_name }}</strong>
                <span><template v-if="note.note_date">{{ shortDate(note.note_date) }} · </template>{{ note.note }}<template v-if="note.country"> · {{ note.country }}</template></span>
              </div>
              <div>
                <v-btn icon="mdi-pencil-outline" size="x-small" variant="text" aria-label="Edit note" @click="editNote(note)" />
                <v-btn icon="mdi-delete-outline" size="x-small" variant="text" color="error" aria-label="Delete note" @click="deleteNote(note)" />
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn variant="text" @click="notesDialogOpen = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2800">{{ snackbar.message }}</v-snackbar>
  </main>
</template>

<script>
import { addDays, format, parseISO } from 'date-fns';
import secureAxios from '@/utils/secureAxios';

const SITE_ORDER = ['Flirt4Free', 'Streamate', 'LiveJasmin', 'Stripchat', 'Chaturbate'];
const blankDraft = () => ({ name: '', payment_date: '', cb_payment_date: '', cb_skipped: false, is_locked: false, ranges: SITE_ORDER.map((website) => ({ website, start_date: '', end_date: '' })) });
const blankNote = () => ({ period_id: null, note_type: 'Holiday', note_date: '', note: '', country: 'Colombia' });

export default {
  name: 'PeriodsPage',
  data() {
    return {
      currentYear: new Date().getFullYear(),
      selectedYear: new Date().getFullYear(),
      periods: [],
      canEdit: false,
      loading: false,
      saving: false,
      error: '',
      dialogOpen: false,
      editingId: null,
      draft: blankDraft(),
      notesDialogOpen: false,
      editingNoteId: null,
      savingNote: false,
      noteForm: blankNote(),
      snackbar: { show: false, message: '', color: 'success' },
    };
  },
  watch: {
    selectedYear() { this.loadPeriods(); },
  },
  computed: {
    currentPeriodIndex() {
      if (!this.periods.length || this.selectedYear !== new Date().getFullYear()) return -1;
      const today = format(new Date(), 'yyyy-MM-dd');
      const index = this.periods.findIndex((period) => {
        const paymentDate = this.iso(period.payment_date);
        return paymentDate && today <= paymentDate;
      });
      return index >= 0 ? index : this.periods.length - 1;
    },
    featuredPeriods() {
      const currentIndex = this.currentPeriodIndex;
      if (currentIndex < 0) return [];
      return [
        { kind: 'current', label: 'Current Period', period: this.periods[currentIndex] },
        ...(this.periods[currentIndex + 1]
          ? [{ kind: 'next', label: 'Next Period', period: this.periods[currentIndex + 1] }]
          : []),
      ];
    },
    yearNotes() {
      return this.periods.flatMap((period) => (period.notes || []).map((note) => ({ ...note, period_name: period.name })));
    },
  },
  created() { this.loadPeriods(); },
  methods: {
    async loadPeriods() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await secureAxios.get('/api/period-calendar', { params: { year: this.selectedYear } });
        this.periods = data.periods || [];
        this.canEdit = data.can_edit === true;
      } catch (error) {
        this.periods = [];
        this.error = error.response?.data?.message || 'Unable to load periods.';
      } finally { this.loading = false; }
    },
    changeYear(amount) { this.selectedYear += amount; },
    clonePeriod(period) {
      return {
        name: period.name,
        payment_date: this.iso(period.payment_date),
        cb_payment_date: this.iso(period.cb_payment_date),
        cb_skipped: !period.cb_payment_date,
        is_locked: !!period.is_locked,
        ranges: SITE_ORDER.map((website) => {
          const found = period.ranges.find((range) => range.website === website) || {};
          return { website, start_date: this.iso(found.start_date), end_date: this.iso(found.end_date) };
        }),
      };
    },
    openEdit(period) {
      this.editingId = period.id;
      this.draft = this.clonePeriod(period);
      this.dialogOpen = true;
    },
    openNextPeriod() {
      const source = this.periods[this.periods.length - 1];
      if (!source) return;
      const draft = blankDraft();
      const previousNumber = Number(String(source.name).split('-')[0]) || 0;
      const nextF4fStart = this.addDays(this.rangeFor(source, 'Flirt4Free').end_date, 1);
      const nextYear = Number(nextF4fStart.slice(0, 4)) + (previousNumber >= 26 ? 1 : 0);
      const nextNumber = previousNumber >= 26 ? 1 : previousNumber + 1;
      draft.name = `${nextNumber}--${nextYear}`;
      draft.payment_date = this.addDays(this.iso(source.payment_date), 14);
      draft.ranges = SITE_ORDER.map((website) => {
        const previous = this.rangeFor(source, website);
        const start = this.addDays(previous.end_date, 1);
        let end = this.addDays(start, 13);
        if (website === 'Chaturbate') {
          const startDate = parseISO(start);
          end = startDate.getDate() === 1
            ? format(new Date(startDate.getFullYear(), startDate.getMonth(), 15), 'yyyy-MM-dd')
            : format(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0), 'yyyy-MM-dd');
        }
        return { website, start_date: start, end_date: end };
      });
      draft.cb_payment_date = this.addDays(this.rangeFor(draft, 'Chaturbate').end_date, 4);
      this.editingId = null;
      this.draft = draft;
      this.dialogOpen = true;
    },
    rangeFor(period, website) { return period.ranges.find((range) => range.website === website) || { start_date: '', end_date: '' }; },
    async savePeriod() {
      this.saving = true;
      try {
        const response = this.editingId
          ? await secureAxios.put(`/api/period-calendar/${this.editingId}`, this.draft)
          : await secureAxios.post('/api/period-calendar', this.draft);
        this.notify(response.data.message || 'Period saved');
        this.dialogOpen = false;
        const year = Number(String(this.draft.name).match(/(20\d{2})$/)?.[1]);
        if (year && year !== this.selectedYear) this.selectedYear = year;
        else await this.loadPeriods();
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save period.', 'error');
      } finally { this.saving = false; }
    },
    openNotes() {
      this.resetNoteForm();
      this.noteForm.period_id = this.periods.find((period) => this.isCurrentPeriod(period))?.id || this.periods[0]?.id || null;
      this.notesDialogOpen = true;
    },
    resetNoteForm() {
      this.editingNoteId = null;
      this.noteForm = blankNote();
    },
    editNote(note) {
      this.editingNoteId = note.id;
      this.noteForm = {
        period_id: note.period_id,
        note_type: note.note_type,
        note_date: this.iso(note.note_date),
        note: note.note,
        country: note.country || '',
      };
    },
    async saveNote() {
      if (!this.noteForm.period_id || !this.noteForm.note || (this.noteForm.note_type === 'Holiday' && !this.noteForm.note_date)) {
        return this.notify('Select a period and enter the holiday or note details.', 'warning');
      }
      this.savingNote = true;
      try {
        const response = this.editingNoteId
          ? await secureAxios.put(`/api/period-calendar/notes/${this.editingNoteId}`, this.noteForm)
          : await secureAxios.post('/api/period-calendar/notes', this.noteForm);
        this.notify(response.data.message || 'Period note saved');
        await this.loadPeriods();
        this.resetNoteForm();
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to save period note.', 'error');
      } finally { this.savingNote = false; }
    },
    async deleteNote(note) {
      if (!window.confirm(`Delete “${note.note}”?`)) return;
      try {
        const { data } = await secureAxios.delete(`/api/period-calendar/notes/${note.id}`);
        this.notify(data.message || 'Period note deleted');
        await this.loadPeriods();
        if (this.editingNoteId === note.id) this.resetNoteForm();
      } catch (error) {
        this.notify(error.response?.data?.message || 'Unable to delete period note.', 'error');
      }
    },
    paymentsAligned(period) {
      return !!period.payment_date && !!period.cb_payment_date && this.iso(period.payment_date) === this.iso(period.cb_payment_date);
    },
    isCurrentPeriod(period) {
      return this.currentPeriodIndex >= 0
        && Number(this.periods[this.currentPeriodIndex]?.id) === Number(period.id);
    },
    iso(value) { return value ? String(value).slice(0, 10) : ''; },
    addDays(value, amount) { return value ? format(addDays(parseISO(this.iso(value)), amount), 'yyyy-MM-dd') : ''; },
    dateLabel(value) { return value ? format(parseISO(this.iso(value)), 'MMM d, yyyy') : '—'; },
    shortDate(value) { return value ? format(parseISO(this.iso(value)), 'MMM d') : ''; },
    notify(message, color = 'success') { this.snackbar = { show: true, message, color }; },
  },
};
</script>

<style scoped>
.periods-page{max-width:1500px;margin:0 auto;padding:22px 22px 70px;color:#203247}.page-heading,.page-actions,.period-controls,.year-control,.period-card-heading,.period-card-actions,.ranges-heading{display:flex;align-items:center}.page-heading{justify-content:space-between;align-items:flex-end;gap:20px;margin:4px 2px 18px}.page-actions{gap:9px}.eyebrow{color:#2f7db9;font-size:11px;font-weight:800;letter-spacing:.14em}.page-heading h1{margin:3px 0 4px;font-size:28px;line-height:1}.page-heading p{margin:0;color:#64768a;font-size:13px}.period-controls{display:grid;grid-template-columns:1fr 1.5fr 1fr;min-height:70px;padding:7px 13px;margin-bottom:18px;border:1px solid #d8dfe6;border-radius:7px}.year-control{justify-content:flex-start}.year-control strong{min-width:70px;color:#174d78;font-size:21px;text-align:center}.system-title{text-align:center}.system-title small,.system-title strong{display:block}.system-title small{color:#748598;font-size:9px;font-weight:800;letter-spacing:.12em}.system-title strong{font-size:22px}.period-controls>.v-btn{justify-self:end}.featured-periods{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px 22px;margin-bottom:24px;padding:14px;border:1px solid #bfd5e6;border-radius:9px;background:linear-gradient(135deg,#f4f9fd,#edf5fb)}.featured-label{display:flex;align-items:center;gap:6px;margin:0 2px 7px;color:#1f6598;font-size:11px;font-weight:900;letter-spacing:.08em;text-transform:uppercase}.featured-period.next .featured-label{color:#597084}.featured-card{background:#fff}.featured-card .period-card-heading{border-bottom:1px solid #c7d9e7;background:#fff;color:#1e3447}.featured-period.current .featured-card{border-color:#2f86c6;box-shadow:0 2px 9px rgba(35,102,151,.14)}.all-periods-heading{display:flex;align-items:center;gap:10px;margin:0 2px 10px;color:#60758a;font-size:10px;font-weight:900;letter-spacing:.11em}.all-periods-heading::after{height:1px;flex:1;background:#d7e0e7;content:''}.period-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px 22px}.period-card{border:1px solid #d9e0e6;border-radius:7px;overflow:hidden}.period-card.current{border-color:#2f86c6;box-shadow:0 0 0 1px rgba(47,134,198,.18)}.period-card-heading{min-height:45px;justify-content:space-between;padding:7px 10px;background:#17232c;color:#fff}.period-card-heading>div:first-child{display:flex;align-items:baseline;gap:9px}.period-card-heading strong{font-size:14px}.period-card-heading span{color:#9ed2fa;font-size:9px;font-weight:800;letter-spacing:.06em;text-transform:uppercase}.period-card-actions{gap:3px}.period-card table{width:100%;border-collapse:collapse;table-layout:fixed}.period-card th{padding:5px 7px;background:#a9cdea;color:#143755;font-size:10px;text-align:left}.period-card th:first-child{width:20%}.period-card td{padding:5px 7px;border-bottom:1px solid #e2e6e9;color:#273746;font-size:10px}.period-card tbody tr:nth-child(odd){background:#f3f5f7}.period-card tbody tr:last-child td{border-bottom:0}.period-card td:first-child{font-weight:750}.period-card td.cb-payment{color:#8b5b12;font-weight:800}.loading-state,.empty-state{display:flex;min-height:300px;flex-direction:column;align-items:center;justify-content:center;gap:10px;color:#718094}.empty-state{border:1px dashed #ccd5dd}.period-details-fields{display:grid;grid-template-columns:1.3fr 1fr 1fr;gap:10px}.ranges-heading{justify-content:space-between;margin:3px 0 10px}.ranges-heading strong,.ranges-heading small{display:block}.ranges-heading small{margin-top:2px;color:#728194;font-size:10px}.range-editor{overflow:hidden;border:1px solid #dae1e7;border-radius:6px}.range-editor-head,.range-editor-row{display:grid;grid-template-columns:1.15fr 1fr 1fr;align-items:center;gap:8px;padding:7px 10px}.range-editor-head{background:#edf3f7;color:#516678;font-size:9px;font-weight:850;text-transform:uppercase}.range-editor-row{border-top:1px solid #e1e7ec}.range-editor-row strong{font-size:12px}.period-dialog :deep(.v-card-subtitle){white-space:normal}.period-dialog :deep(.v-card-actions){padding:10px 18px 16px}@media(max-width:1000px){.featured-periods,.period-grid{grid-template-columns:1fr}.period-controls{grid-template-columns:1fr}.system-title{grid-row:1}.year-control{justify-content:center}.period-controls>.v-btn{justify-self:center}.page-heading{align-items:flex-start;flex-direction:column}}@media(max-width:650px){.periods-page{padding:18px 10px 55px}.featured-periods{padding:9px}.period-details-fields{grid-template-columns:1fr}.range-editor{overflow-x:auto}.range-editor-head,.range-editor-row{min-width:620px}}
.period-notes{display:flex;flex-wrap:wrap;column-gap:24px;row-gap:2px;padding:6px 9px;border-top:1px solid #dce3e8;background:#fbfcfd}.period-note{display:flex;align-items:flex-start;gap:5px;padding:2px 0;color:#694f18;font-size:9px;line-height:1.3}.period-note.holiday-note{flex:0 1 auto}.period-note strong{font-weight:850}.period-note.general-note,.period-note.aligned-note{flex-basis:100%}.period-note.general-note{color:#53687a}.period-note.aligned-note{color:#2e7d32;font-weight:800}.notes-dialog :deep(.v-card-subtitle){white-space:normal}.note-form{display:grid;grid-template-columns:1.1fr .75fr 1fr 1fr;align-items:start;gap:8px}.note-text-field{grid-column:1/4}.notes-list{max-height:330px;overflow-y:auto;border-top:1px solid #e0e5ea}.notes-list-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 3px;border-bottom:1px solid #e8ecef}.notes-list-row strong,.notes-list-row span{display:block}.notes-list-row strong{font-size:10px;color:#52708a}.notes-list-row span{margin-top:2px;font-size:11px}.notes-list-row>div:last-child{display:flex}@media(max-width:650px){.note-form{grid-template-columns:1fr}.note-text-field{grid-column:auto}}
</style>
